import {createSelector} from 'reselect';

import {getUIState} from '../../../ui-selectors';

const getCompletedActionsState = createSelector(
    [getUIState],
    (uiState) => uiState.completedActions
);

const getCompletedActionsChartState = createSelector(
    [getCompletedActionsState],
    (completedActions) => completedActions.timeseriesChart
);

export const getCurrentReportChartIsLoading = createSelector(
    [getCompletedActionsChartState],
    (timeseriesChartState) => timeseriesChartState.isLoading
);

export const isChartTimedOut = createSelector(
    [getCompletedActionsChartState],
    (timeseriesChartState) => timeseriesChartState.isTimedOut
);

export const isChartErrored = createSelector(
    [getCompletedActionsChartState],
    (timeseriesChartState) => timeseriesChartState.isErrored
);

export const getCurrentReportChartData = createSelector(
    [getCompletedActionsChartState],
    (timeseriesChartState) => timeseriesChartState.chartData
);

export const getCurrentReportChartBucketCount = createSelector(
    [getCurrentReportChartData],
    (chartData) => (chartData ? chartData.buckets.length : 1)
);

// Alias for ReportDataSummary compatibility
export const getChartBucketCount = getCurrentReportChartBucketCount;

export const getIsLoading = getCurrentReportChartIsLoading;

export const getSummaryStats = createSelector([getCurrentReportChartData], (chartData) => {
    const defaultStats = {
        actions: {sum: {value: 0, formatted: '0', prefix: '', suffix: ''}},
    } as const;

    if (!chartData || !chartData.reports || !chartData.reports[0] || !chartData.summaries) {
        return defaultStats;
    }

    const reportId = chartData.reports[0].id;
    const summary = chartData.summaries[reportId];

    if (!summary || summary.count === undefined) {
        return defaultStats;
    }

    return {
        actions: {
            sum: {
                value: summary.count,
                formatted: String(summary.count),
                prefix: '',
                suffix: '',
            },
        },
    };
});

export type ActionsBreakdown = {
    [actionType: string]: number;
};

export const getActionsBreakdown = createSelector(
    [getCurrentReportChartData],
    (chartData): ActionsBreakdown => {
        const defaultBreakdown: Record<string, any> = {};

        if (!chartData || !chartData.reports || !chartData.reports[0] || !chartData.summaries) {
            return defaultBreakdown;
        }

        const reportId = chartData.reports[0].id;
        const summary = chartData.summaries[reportId];

        if (!summary || !summary.segments) {
            return defaultBreakdown;
        }

        // Extract count for each action type from segments
        const breakdown: ActionsBreakdown = {};
        Object.keys(summary.segments).forEach((actionType) => {
            breakdown[actionType] = summary.segments[actionType].count || 0;
        });

        return breakdown;
    }
);
